import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { Text, TextInput } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramPitotTraverseAveraging } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { calcVelocityFpmFromVelocityPressure } from '../../../lib/calcs';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.pitotTraverseAveraging.v1';

type PersistedFields = { density: string; ductArea: string };
const EMPTY: PersistedFields = { density: '', ductArea: '' };

function parseNumberList(raw: string): number[] {
  return raw
    .split(/[^0-9eE.+-]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

const AirflowPitotTraverseAveragingScreen = () => {
  const [velocityPressures, setVelocityPressures] = useState('');
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<PersistedFields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const [avgVp, setAvgVp] = useState('');
  const [avgVelocity, setAvgVelocity] = useState('');
  const [avgFlow, setAvgFlow] = useState('');

  const set = (patch: Partial<PersistedFields>) => setFields((prev) => ({ ...prev, ...patch }));

  const readings = useMemo(() => parseNumberList(velocityPressures), [velocityPressures]);

  const calculate = () => {
    setError(null);
    // Density always in Imperial (lb/ft³) for formula. Blank → standard air.
    const rho = fields.density.trim() === '' ? 0.075 : toImp(fields.density, 'density');
    if (!Number.isFinite(rho) || rho <= 0) {
      setError('Air density must be a number greater than zero');
      return;
    }
    if (readings.length < 2) {
      setError('Enter at least 2 velocity pressure readings');
      return;
    }
    // Readings are always in in. w.g. (Imperial) on a Pitot traverse
    // We store/display them in pressure units
    const pvReadingsImp = readings.map((r) => toImp(String(r), 'pressure'));
    if (pvReadingsImp.some((v) => v < 0)) {
      setError('Velocity pressure readings cannot be negative');
      return;
    }

    const vpAvgImp = pvReadingsImp.reduce((sum, v) => sum + v, 0) / pvReadingsImp.length;
    const vAvgFpm = calcVelocityFpmFromVelocityPressure(vpAvgImp, rho);
    if (!Number.isFinite(vAvgFpm)) {
      setError('Unable to compute velocity from the given inputs');
      return;
    }

    setAvgVp(fromImp(vpAvgImp, 'pressure', 4));
    setAvgVelocity(fromImp(vAvgFpm, 'velocity', 1));

    if (fields.ductArea.trim() !== '') {
      const areaFt2 = toImp(fields.ductArea, 'area');
      if (!Number.isFinite(areaFt2) || areaFt2 <= 0) {
        setError('Duct area must be a number greater than zero');
        return;
      }
      setAvgFlow(fromImp(vAvgFpm * areaFt2, 'flow', 1));
    } else {
      setAvgFlow('');
    }
  };

  const clearAll = () => {
    setError(null);
    void clearPersisted();
    setVelocityPressures('');
    setAvgVp('');
    setAvgVelocity('');
    setAvgFlow('');
  };

  const result = (avgVp || avgVelocity || avgFlow)
    ? [
        avgVp ? { label: 'Average Pv', value: avgVp, unit: unit('pressure') } : null,
        avgVelocity ? { label: 'Average Velocity', value: avgVelocity, unit: unit('velocity') } : null,
        avgFlow ? { label: 'Airflow', value: avgFlow, unit: unit('flow'), emphasis: true as const } : null,
      ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[]
    : null;

  return (
    <CalcShell
      title="Pitot Traverse Averaging"
      subtitle="Average velocity pressure readings and calculate average velocity (and flow)"
      formula={
        <>
          <FormulaLine>Pv(avg) = (Pv1 + Pv2 + … + Pvn) / n</FormulaLine>
          <FormulaLine>V(avg) = 4005 × √(Pv(avg) × (0.075 / ρ))</FormulaLine>
          <FormulaLine>Flow = V(avg) × Area</FormulaLine>
          <FormulaNote>Pv = velocity pressure ({unit('pressure')}), ρ = air density ({unit('density')}). Standard air: {isImperial ? '0.075 lb/ft³' : '1.2 kg/m³'}.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result}
      copyFooter="Formula: Pv(avg) = (Pv1 + … + Pvn) / n; V = 4005 × √(Pv × 0.075/ρ)"
      diagram={<DiagramPitotTraverseAveraging />}
      outputSlots={{
        air_density_lb_ft3: fields.density ? toImp(fields.density, 'density') : undefined,
        velocity_pressure_iwg: avgVp ? toImp(avgVp, 'pressure') : undefined,
        velocity_fpm: avgVelocity ? toImp(avgVelocity, 'velocity') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<PersistedFields> = {};
        if (values.air_density_lb_ft3 != null && !fields.density) {
          patch.density = fromImp(values.air_density_lb_ft3, 'density', 4);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <TextInput
          label={`Velocity Pressure Readings (${unit('pressure')})`}
          value={velocityPressures}
          onChangeText={setVelocityPressures}
          mode="outlined"
          multiline
          numberOfLines={4}
          placeholder="Example: 0.012, 0.011, 0.013, 0.010"
          style={{ backgroundColor: '#1a1a1a' }}
          textColor="#FFFFFF"
          placeholderTextColor="#666666"
          outlineColor="#444444"
          activeOutlineColor="#007AFF"
        />
        {readings.length > 0 && (
          <Text style={{ color: '#AAAAAA', fontSize: 12, marginTop: 4 }}>
            Readings parsed: {readings.length}
          </Text>
        )}
      </View>

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Air Density ρ"
          value={fields.density}
          onChangeText={(v) => set({ density: v })}
          rightText={unit('density')}
          maxDecimals={4}
          placeholder={isImperial ? '0.075 standard air' : '1.2 standard air'}
          onSubmitEditing={calculate}
        />
      </View>

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Duct Area (optional)"
          value={fields.ductArea}
          onChangeText={(v) => set({ ductArea: v })}
          rightText={unit('area')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowPitotTraverseAveragingScreen;
